import React, { useState, useEffect } from "react";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import { Button } from "../ui/components/Button";
import { Select } from "../ui/components/Select";
import { TextArea } from "../ui/components/TextArea";
import { FeatherInfo } from "@subframe/core";
import { FeatherPause } from "@subframe/core";

interface PauseRecurringJournalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (pauseReason: string, pauseDuration?: string) => void;
  loading?: boolean;
  recurrenceFrequency?: string; // start_of_month, end_of_month, daily, every_n_days
}

const PauseRecurringJournalModal: React.FC<PauseRecurringJournalModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  loading = false,
  recurrenceFrequency = "daily",
}) => {
  const [pauseDuration, setPauseDuration] = useState<string>("");
  const [pauseReason, setPauseReason] = useState("");
  const [resumeDate, setResumeDate] = useState<string>("");
  const [nextEntryDate, setNextEntryDate] = useState<string>("");

  // Calculate resume date and next entry date based on duration and recurrence frequency
  useEffect(() => {
    if (!pauseDuration) {
      setResumeDate("");
      setNextEntryDate("");
      return;
    }

    const today = new Date();
    const monthsToAdd = parseInt(pauseDuration);
    let resumeTargetDate: Date;
    let nextEntryTargetDate: Date;

    // Simple logic: Resume after pauseDuration months, next entry after that
    if (recurrenceFrequency === "start_of_month") {
      // Resume date: Add pauseDuration months to today
      resumeTargetDate = new Date(today);
      resumeTargetDate.setMonth(today.getMonth() + monthsToAdd);

      // Next entry: First day of month AFTER the resume month
      // Example: Pause Oct 15 for 1 month → Resume Nov 15, Next entry Dec 1
      nextEntryTargetDate = new Date(
        today.getFullYear(),
        today.getMonth() + monthsToAdd + 1,
        1
      );
    } else if (recurrenceFrequency === "end_of_month") {
      // Resume date: Add pauseDuration months to today
      resumeTargetDate = new Date(today);

      resumeTargetDate.setMonth(today.getMonth() + monthsToAdd);

      // Next entry: Last day of the month that comes AFTER the pause period
      // Example: Pause Oct 15 for 1 month → Resume Nov 15, Next entry Nov 30 (end of November)
      // We skip the current month (Oct) since we're pausing, and process at the end of the next month (Nov)
      // To get last day of month m (0-indexed): new Date(year, m+1, 0)
      // For Nov (month 10): new Date(year, 11, 0) = last day of November
      // So: today.getMonth() (9 for Oct) + monthsToAdd (1) + 1 = 11 → last day of Nov
      nextEntryTargetDate = new Date(
        today.getFullYear(),
        today.getMonth() + monthsToAdd + 1,
        0
      );
    } else {
      // For daily and every_n_days: Resume and next entry are the same
      resumeTargetDate = new Date(today);
      resumeTargetDate.setMonth(today.getMonth() + monthsToAdd);
      nextEntryTargetDate = new Date(resumeTargetDate);
    }

    const formattedResumeDate = resumeTargetDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const formattedNextEntryDate = nextEntryTargetDate.toLocaleDateString(
      "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );

    setResumeDate(formattedResumeDate);
    setNextEntryDate(formattedNextEntryDate);
  }, [pauseDuration, recurrenceFrequency]);

  // Handle form submission
  const handleSubmit = () => {
    if (!pauseReason.trim()) {
      alert("Please provide a reason for pausing");
      return;
    }

    // if (!pauseDuration) {
    //   alert("Please select a pause duration");
    //   return;
    // }

    onSubmit(pauseReason, pauseDuration);
  };

  // Handle close
  const handleClose = () => {
    setPauseDuration("");
    setPauseReason("");
    setResumeDate("");
    setNextEntryDate("");
    onOpenChange(false);
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex h-full w-full flex-col items-start gap-6 px-6 py-6">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Pause Journal Entry
          </span>
          <span className="text-body font-body text-subtext-color">
            Temporarily pause this recurring journal entry. You can resume it at
            any time.
          </span>
        </div>
        <div className="flex w-full flex-col items-start gap-6">
          <Select
            className="h-auto w-full flex-none"
            label="Pause Duration"
            placeholder="Select duration"
            helpText=""
            value={pauseDuration}
            onValueChange={(value: string) => setPauseDuration(value)}
          >
            <Select.Item value="1">1 Month</Select.Item>
            <Select.Item value="2">2 Months</Select.Item>
            <Select.Item value="3">3 Months</Select.Item>
            <Select.Item value="4">4 Months</Select.Item>
            <Select.Item value="5">5 Months</Select.Item>
            <Select.Item value="6">6 Months</Select.Item>
            <Select.Item value="7">7 Months</Select.Item>
            <Select.Item value="8">8 Months</Select.Item>
            <Select.Item value="9">9 Months</Select.Item>
            <Select.Item value="10">10 Months</Select.Item>
            <Select.Item value="11">11 Months</Select.Item>
            <Select.Item value="12">12 Months</Select.Item>
          </Select>
          {resumeDate && (
            <div className="flex w-full flex-col items-start gap-3 rounded-md bg-brand-50 px-4 py-3 border border-solid border-brand-200">
              <div className="flex items-center gap-2">
                <FeatherInfo className="text-body font-body text-brand-700" />
                <span className="text-body-bold font-body-bold text-brand-700">
                  Pause Summary
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-2">
                {(recurrenceFrequency === "start_of_month" ||
                  recurrenceFrequency === "end_of_month") && (
                  <span className="text-body font-body text-brand-700">
                    • Next journal entry will be created on:{" "}
                    <span className="font-bold">{nextEntryDate}</span>
                  </span>
                )}
                {(recurrenceFrequency === "daily" ||
                  recurrenceFrequency === "every_n_days") && (
                  <span className="text-body font-body text-brand-700">
                    • Template will resume on:{" "}
                    <span className="font-bold">{resumeDate}</span>
                  </span>
                )}
              </div>
            </div>
          )}
          <TextArea
            className="h-auto w-full flex-none"
            label="Reason for Pausing"
            helpText="Please provide a reason for pausing this journal entry"
          >
            <TextArea.Input
              placeholder="Enter reason for pausing..."
              value={pauseReason}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                setPauseReason(event.target.value)
              }
            />
          </TextArea>
        </div>
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            variant="neutral-tertiary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            icon={<FeatherPause />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Pausing..." : "Pause Journal"}
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default PauseRecurringJournalModal;
